import { useLanguage } from '@/contexts/LanguageContext';
import { motion } from 'framer-motion';
import { Quote } from 'lucide-react';
import { useEffect, useMemo, useRef, useState } from 'react';
import Autoplay from 'embla-carousel-autoplay';
import { supabase } from '@/integrations/supabase/client';
import { extractYouTubeId } from '@/lib/youtube';
import YouTube, { type YouTubeEvent, type YouTubePlayer } from 'react-youtube';
import {
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselNext,
  CarouselPrevious,
  type CarouselApi,
} from '@/components/ui/carousel';

interface DbTestimonial {
  name: string;
  quote: string;
}

interface DbTestimonialVideo {
  id: string;
  video_url: string;
  title: string;
  source_type: string;
}

const Testimonials = () => {
  const { t } = useLanguage();
  const [dbTestimonials, setDbTestimonials] = useState<DbTestimonial[]>([]);
  const [videos, setVideos] = useState<DbTestimonialVideo[]>([]);
  const [api, setApi] = useState<CarouselApi>();
  const [current, setCurrent] = useState(0);
  const videoRefs = useRef<(HTMLVideoElement | null)[]>([]);
  const ytPlayersRef = useRef<Record<string, YouTubePlayer | null>>({});
  const [ytPlaying, setYtPlaying] = useState<Record<string, boolean>>({});

  const autoplayRef = useRef(
    Autoplay({ delay: 12000, stopOnInteraction: true, stopOnMouseEnter: true }),
  );
  const plugins = useMemo(() => [autoplayRef.current], []);
  const [autoplayPaused, setAutoplayPaused] = useState(false);

  const stopAutoplay = () => {
    autoplayRef.current.stop();
    setAutoplayPaused(true);
  };
  const resumeAutoplay = () => {
    autoplayRef.current.play();
    setAutoplayPaused(false);
  };

  useEffect(() => {
    const fetchAll = async () => {
      const [{ data: tData }, { data: vData }] = await Promise.all([
        supabase.from('testimonials').select('name, quote').eq('visible', true).order('sort_order'),
        supabase
          .from('testimonial_videos')
          .select('id, video_url, title, source_type')
          .eq('visible', true)
          .order('sort_order'),
      ]);
      if (tData && tData.length > 0) setDbTestimonials(tData);
      if (vData) setVideos(vData);
    };
    fetchAll();
  }, []);

  useEffect(() => {
    if (!api) return;
    setCurrent(api.selectedScrollSnap());
    api.on('select', () => setCurrent(api.selectedScrollSnap()));
  }, [api]);

  // Play active slide's video, pause the others
  useEffect(() => {
    videoRefs.current.forEach((video, idx) => {
      if (!video) return;
      if (idx === current) {
        video.play().catch(() => {
          /* autoplay may be blocked until user interaction; ignore */
        });
      } else {
        video.pause();
        video.currentTime = 0;
      }
    });
    // Reset YouTube embeds when slide changes — pouze aktivní slide může mít iframe
    setYtPlaying({});
  }, [current, videos.length]);

  // Pause everything when the section leaves the viewport
  const sectionRef = useRef<HTMLElement | null>(null);
  useEffect(() => {
    const node = sectionRef.current;
    if (!node) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          const active = videoRefs.current[current];
          active?.play().catch(() => {});
        } else {
          videoRefs.current.forEach((v) => v?.pause());
          Object.values(ytPlayersRef.current).forEach((p) => {
            try { p?.pauseVideo?.(); } catch { /* ignore */ }
          });
        }
      },
      { threshold: 0.25 },
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, [current, videos.length]);

  const testimonialItems = dbTestimonials.length > 0 ? dbTestimonials : t.testimonials.items;

  return (
    <section ref={sectionRef} className="py-20 md:py-32 px-4">
      <div className="max-w-6xl mx-auto">
        {/* Video References - Carousel (only if at least one video) */}
        {videos.length > 0 && (
          <>
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="text-center mb-12"
            >
              <h2
                className="text-5xl md:text-7xl tracking-wider text-foreground"
                style={{ fontFamily: 'Bebas Neue' }}
              >
                {t.testimonials.videoTitle}
              </h2>
            </motion.div>

            <div className="mb-20 px-4 md:px-16 relative">
              {/* Decorative Ice Blue glow behind the active video card */}
              <div
                aria-hidden
                className="pointer-events-none absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-[360px] h-[520px] md:w-[420px] md:h-[600px] rounded-[40%] bg-primary/40 blur-[80px]"
              />
              <div
                aria-hidden
                className="pointer-events-none absolute left-1/2 bottom-0 -translate-x-1/2 translate-y-1/3 w-[420px] h-[180px] rounded-full bg-primary/50 blur-[70px]"
              />
              <Carousel
                setApi={setApi}
                opts={{ loop: true, align: 'center' }}
                plugins={plugins}
                className="max-w-md mx-auto relative z-10"
              >
                <CarouselContent>
                  {videos.map((v, idx) => {
                    const isActive = idx === current;
                    const isAdjacent =
                      Math.abs(idx - current) === 1 ||
                      Math.abs(idx - current) === videos.length - 1;
                    const preload = isActive ? 'auto' : isAdjacent ? 'metadata' : 'none';
                    const isYouTube = v.source_type === 'youtube';
                    const ytId = isYouTube ? extractYouTubeId(v.video_url) : null;
                    return (
                      <CarouselItem key={v.id}>
                        <div className={`rounded-sm border border-border/50 bg-card/30 backdrop-blur-sm overflow-hidden ring-1 ring-border/50 transition-shadow duration-500 ${isActive ? 'shadow-[0_25px_60px_-15px_hsl(var(--primary)/0.6)] ring-primary/40' : 'shadow-[0_20px_50px_-20px_hsl(var(--primary)/0.45)]'}`}>
                          <div
                            className="relative w-full bg-black"
                            style={{ aspectRatio: '9 / 16' }}
                          >
                            {isYouTube && ytId ? (
                              isActive && ytPlaying[v.id] ? (
                                <YouTube
                                  videoId={ytId}
                                  title={v.title}
                                  iframeClassName="absolute inset-0 w-full h-full"
                                  className="absolute inset-0 w-full h-full"
                                  opts={{
                                    playerVars: {
                                      autoplay: 1,
                                      rel: 0,
                                      modestbranding: 1,
                                      playsinline: 1,
                                    },
                                  }}
                                  onReady={(e: YouTubeEvent) => {
                                    ytPlayersRef.current[v.id] = e.target;
                                    stopAutoplay();
                                  }}
                                  onPause={() => {
                                    resumeAutoplay();
                                  }}
                                  onEnd={() => {
                                    setYtPlaying((p) => {
                                      const next = { ...p };
                                      delete next[v.id];
                                      return next;
                                    });
                                    delete ytPlayersRef.current[v.id];
                                    resumeAutoplay();
                                    api?.scrollNext();
                                  }}
                                />
                              ) : (
                                <button
                                  type="button"
                                  onClick={() => {
                                    stopAutoplay();
                                    setYtPlaying((p) => ({ ...p, [v.id]: true }));
                                  }}
                                  className="absolute inset-0 w-full h-full group"
                                  aria-label={`Přehrát video ${v.title}`}
                                >
                                  <img
                                    src={`https://img.youtube.com/vi/${ytId}/hqdefault.jpg`}
                                    alt={v.title}
                                    loading="lazy"
                                    className={`absolute inset-0 w-full h-full object-cover transition-opacity ${isActive ? 'opacity-100' : 'opacity-80'}`}
                                  />
                                  {isActive && (
                                    <span className="absolute inset-0 flex items-center justify-center bg-black/30 group-hover:bg-black/10 transition-colors">
                                      <span className="w-16 h-16 rounded-full bg-primary/90 backdrop-blur-sm flex items-center justify-center shadow-[0_0_25px_hsl(var(--primary)/0.6)] group-hover:scale-110 transition-transform">
                                        <svg width="28" height="28" viewBox="0 0 24 24" fill="currentColor" className="text-primary-foreground ml-1">
                                          <path d="M8 5v14l11-7z" />
                                        </svg>
                                      </span>
                                    </span>
                                  )}
                                </button>
                              )
                            ) : (
                              <video
                                ref={(el) => (videoRefs.current[idx] = el)}
                                src={v.video_url}
                                title={v.title}
                                controls
                                playsInline
                                muted
                                preload={preload}
                                onPlay={stopAutoplay}
                                onPause={() => {
                                  const el = videoRefs.current[idx];
                                  if (el && !el.ended) resumeAutoplay();
                                }}
                                onEnded={() => {
                                  const el = videoRefs.current[idx];
                                  if (el) el.currentTime = 0;
                                  resumeAutoplay();
                                  api?.scrollNext();
                                }}
                                onClick={stopAutoplay}
                                className="absolute inset-0 w-full h-full object-contain"
                              />
                            )}
                          </div>
                        </div>
                      </CarouselItem>
                    );
                  })}
                </CarouselContent>
                <CarouselPrevious className="hidden md:flex" />
                <CarouselNext className="hidden md:flex" />
              </Carousel>

              {/* Stav přehrávání */}
              {autoplayPaused && (
                <p className="text-center text-xs text-muted-foreground/80 mt-4 italic">
                  ⏸ Karusel pozastaven — video se přehrává
                </p>
              )}

              {/* Dots */}
              <div className="flex justify-center gap-2 mt-6">
                {videos.map((_, i) => (
                  <button
                    key={i}
                    onClick={() => api?.scrollTo(i)}
                    aria-label={`Přejít na video ${i + 1}`}
                    className={`h-2 rounded-full transition-all ${
                      current === i ? 'w-6 bg-primary' : 'w-2 bg-muted-foreground/40'
                    }`}
                  />
                ))}
              </div>
            </div>
          </>
        )}

        {/* Text Testimonials */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-12"
        >
          <h2
            className="text-5xl md:text-7xl tracking-wider text-foreground"
            style={{ fontFamily: 'Bebas Neue' }}
          >
            {t.testimonials.title}
          </h2>
        </motion.div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
          {testimonialItems.map((item, i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: i * 0.05 }}
              className="p-6 rounded-sm border border-border/50 bg-card/30 backdrop-blur-sm"
            >
              <Quote className="text-primary/20 mb-3" size={28} />
              <p className="text-foreground/90 italic leading-relaxed text-sm md:text-base">
                "{item.quote}"
              </p>
              <p className="mt-4 text-foreground font-bold text-sm">{item.name}</p>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default Testimonials;
